import { useState, useRef, useCallback, useEffect } from "react";
import axios from "axios";
import { parseAAMVA } from "@/lib/aamva";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Smartphone, ScanLine } from "lucide-react";
import { API } from "@/apiConfig";
import { CustomerDialog } from "@/components/customers/CustomerDialog";
import { requestPhoneFromPinpad, friendlyPinpadError } from "@/lib/pinpadPhone";

const H = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

// POS "Search customer": asks the pinpad (PAX or ModeskP1/Dejavoo, whichever terminal is active)
// for the phone number (or the cashier types it), finds
// the customer and selects them for the sale; offers "Add Customer" when not found.
export function CustomerSearchDialog({ open, onClose, onSelect, stationId }) {
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const lookup = async (p) => {
    const digits = (p || "").replace(/\D/g, "");
    if (digits.length < 7) return toast.error("Enter at least 7 digits");
    setBusy("find"); setNotFound(false);
    try { const r = await axios.get(`${API}/customers/by-phone/${digits}`, H()); onSelect(r.data); toast.success(`${r.data.full_name} selected · ${r.data.points ?? 0} pts`); onClose(); }
    catch (e) { if (e.response?.status === 404) { setNotFound(true); setPhone(digits); } else toast.error(e.response?.data?.detail || "Lookup failed"); }
    setBusy("");
  };
  const pax = async () => {
    setBusy("pax");
    try { const phone = await requestPhoneFromPinpad(stationId); setPhone(phone); await lookup(phone); }
    catch (e) { toast.error(friendlyPinpadError(e)); setBusy(""); }
  };

  return (
    <>
      <Dialog open={open && !addOpen} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-sm" data-testid="customer-search-dialog">
          <DialogHeader><DialogTitle>Search customer</DialogTitle></DialogHeader>
          <Button className="w-full h-12 bg-zinc-900 text-white" disabled={!!busy} onClick={pax} data-testid="customer-search-pax-btn">{busy === "pax" ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Smartphone className="w-4 h-4 mr-2" />} Customer enters phone on pinpad</Button>
          <div className="text-center text-xs text-zinc-400">or type it</div>
          <div className="flex gap-2">
            <Input autoFocus value={phone} onChange={(e) => setPhone(e.target.value)} onKeyDown={(e) => e.key === "Enter" && lookup(phone)} placeholder="Phone number" inputMode="numeric" data-testid="customer-search-phone-input" />
            <Button variant="outline" disabled={!!busy} onClick={() => lookup(phone)} data-testid="customer-search-find-btn">{busy === "find" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Find"}</Button>
          </div>
          {notFound && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900" data-testid="customer-search-not-found">
              No customer with that number. <Button size="sm" className="ml-2 h-7 bg-amber-600 hover:bg-amber-700 text-white" onClick={() => setAddOpen(true)} data-testid="customer-search-add-btn">Add customer</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      <CustomerDialog open={addOpen} onClose={() => setAddOpen(false)} customer={{ phone }} allowCreateOnly stationId={stationId}
        onSaved={(c) => { onSelect(c); toast.success(`${c.full_name} added and selected`); onClose(); }} />
    </>
  );
}

// POS "Age Check": scan the ID, show the age (21+/18+) and the name so the cashier can compare.
export function AgeCheckDialog({ open, onClose }) {
  const [raw, setRaw] = useState("");
  const [res, setRes] = useState(null);
  const timerRef = useRef(null);
  const busyRef = useRef(false);

  // Local AAMVA parse first (instant, no network); backend only as a fallback for odd formats.
  const check = useCallback(async (text) => {
    const payload = (text ?? "").trim();
    if (!payload || busyRef.current) return;
    busyRef.current = true;
    setRaw("");
    const local = parseAAMVA(payload);
    if (local && local.age != null) {
      setRes({
        age: local.age, full_name: local.full_name, state: local.state, license_number: local.license_no,
        dob: local.dob ? local.dob.toLocaleDateString("en-CA") : "", license_expired: local.is_expired,
      });
      busyRef.current = false;
      return;
    }
    try { const r = await axios.post(`${API}/customers/parse-license`, { raw: payload }, H()); setRes(r.data.parsed); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not read that ID"); }
    busyRef.current = false;
  }, []);

  // Auto-run ~120 ms after the scanner stops typing — no Enter needed.
  const lastKeyAtRef = useRef(0);
  const schedule = (v) => {
    clearTimeout(timerRef.current);
    if (v.length >= 40) timerRef.current = setTimeout(() => check(v), 120);
  };
  const onChange = (e) => {
    lastKeyAtRef.current = Date.now();
    setRaw(e.target.value);
    schedule(e.target.value);
  };
  // Scanners send AAMVA field separators as Enter: during a fast burst treat Enter as a newline
  // (keep buffering); a human pressing Enter on a quiet field runs the check immediately.
  const onKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const burst = Date.now() - lastKeyAtRef.current < 60;
    if (burst && raw.length > 0) { const v = raw + "\n"; lastKeyAtRef.current = Date.now(); setRaw(v); schedule(v); return; }
    clearTimeout(timerRef.current);
    check(raw);
  };
  useEffect(() => () => clearTimeout(timerRef.current), []);
  useEffect(() => { if (!open) { setRaw(""); setRes(null); } }, [open]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { setRes(null); onClose(); } }}>
      <DialogContent className="max-w-sm" data-testid="age-check-dialog">
        <DialogHeader><DialogTitle>Age check — scan the ID</DialogTitle></DialogHeader>
        <div className="flex gap-2 items-center bg-zinc-50 border border-dashed border-zinc-300 rounded-lg p-2">
          <ScanLine className="w-5 h-5 text-zinc-500" />
          <textarea autoFocus value={raw} onChange={onChange} onKeyDown={onKeyDown} rows={1} placeholder="Scan the barcode on the back of the ID…" className="flex-1 text-xs font-mono bg-transparent outline-none resize-none" data-testid="age-check-scan-input" />
        </div>
        {res && (
          <div className={`rounded-xl p-4 text-center ${res.age >= 21 ? "bg-emerald-50 border border-emerald-200" : res.age >= 18 ? "bg-amber-50 border border-amber-200" : "bg-red-50 border border-red-200"}`} data-testid="age-check-result">
            <div className="text-5xl font-black" data-testid="age-check-age">{res.age ?? "?"}</div>
            <div className="text-sm font-semibold mt-1">{res.age >= 21 ? "21+ OK — tobacco / lottery / alcohol" : res.age >= 18 ? "18+ only — NO alcohol / tobacco" : "UNDER 18 — refuse"}</div>
            <div className="text-lg font-bold mt-2" data-testid="age-check-name">{res.full_name}</div>
            <div className="text-xs text-zinc-600">DOB {res.dob} · {res.state} {res.license_number}{res.license_expired ? " · LICENSE EXPIRED" : ""}</div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
